import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Eye, ArrowLeft, Share2, Tag } from 'lucide-react';
import { useBlogPost, useRelatedPosts } from '../hooks/useBlogPosts';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { BlogSidebar } from '../components/blog/BlogSidebar';
import { RelatedPosts } from '../components/blog/RelatedPosts';
import { BlogPostDisclaimer } from '../components/blog/BlogPostDisclaimer';
import { formatDate } from '../utils/format/date';

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, isError } = useBlogPost(slug!);

  useEffect(() => {
    if (post) {
      // Update page title and meta description
      document.title = post.seoTitle || post.title;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && post.seoDescription) {
        metaDescription.setAttribute('content', post.seoDescription);
      }
    }
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <ErrorAlert message="Blog post not found" />
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-emerald-900 to-emerald-800 text-white">
        <div className="absolute inset-0">
          <img
            src={post.featuredImage || 'https://images.unsplash.com/photo-1639322537228-f710d846310a'}
            alt={post.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 to-emerald-800/90" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-16">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-emerald-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
          
          <div className="mb-4">
            <span 
              className="px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: post.category.color }}
            >
              {post.category.name}
            </span>
            {post.isAutoGenerated && (
              <span className="ml-2 px-2 py-1 bg-blue-500 text-white rounded-full text-xs">
                AUTO-GENERATED
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-emerald-100">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(post.publishedAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{post.readTime} min read</span>
            </div>
            {post.views && (
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span>{post.views.toLocaleString()} views</span>
              </div>
            )}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Share2 className="h-4 w-4" />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Article Content */}
          <div className="lg:col-span-3">
            <BlogPostDisclaimer post={post} />
            
            <article className="bg-white rounded-lg shadow-sm p-8">
              <div className="prose prose-emerald max-w-none">
                {post.content.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('# ')) {
                    return (
                      <h1 key={index} className="text-3xl font-bold mb-6 text-gray-900">
                        {paragraph.replace('# ', '')}
                      </h1>
                    );
                  }
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={index} className="text-2xl font-semibold mb-4 mt-8 text-gray-900">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith('### ')) {
                    return (
                      <h3 key={index} className="text-xl font-semibold mb-3 mt-6 text-gray-900">
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (paragraph.trim() === '') {
                    return <br key={index} />;
                  }
                  if (paragraph.startsWith('|')) {
                    // Handle table rows - would need proper markdown parser in production
                    return (
                      <div key={index} className="font-mono text-sm bg-gray-50 p-2 rounded">
                        {paragraph}
                      </div>
                    );
                  }
                  return (
                    <p key={index} className="mb-4 leading-relaxed text-gray-700">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Tags:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm
                               cursor-default"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Author:</strong> {post.author}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Published {formatDate(post.publishedAt)} • Last updated {formatDate(post.updatedAt)}
                </p>
              </div>
            </article>

            {/* Related Posts */}
            <div className="mt-12">
              <RelatedPosts currentPost={post} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <BlogSidebar />
          </div>
        </div>
      </div>
    </div>
  );
}